import { makeAutoObservable } from 'mobx'
import Radom, {
  IConversionRate,
  ManagedEvent,
  ManagedEventDepositData,
  ManagedEventTransaction,
  ManagedEventNewSubscription,
  ManagedEventSubscriptionCancelled,
  ManagedEventSubscriptionExpired,
  ManagedEventWithdrawalData,
  OpenBankingPaymentData,
  PaymentSummary
} from './Radom'
import { ManagedPaymentMethod, getMethod, prodNetworks, testNetworks } from '../util/Managed'
import { startOfDay, subDays } from 'date-fns'

export type PaymentGateway = 'Self-custodial' | 'Managed'

interface BaseEvent {
  gateway: PaymentGateway
  date: Date
}

interface LabelledEvent extends BaseEvent {
  url: string
  name: string
  label: string
}

export enum AssociatedEntityType {
  PaymentLink,
  OpenBanking,
  CheckoutSession,
  Invoice,
  PaymentSession,
  Subscription,
  DonationLink
}

interface AssociatedEntity {
  id: string
  entityType: AssociatedEntityType
  link: string
}

export interface ManagedPaymentEvent extends LabelledEvent {
  id: string
  gateway: 'Managed'
  name: 'PaymentEvent' | 'RecurringPaymentEvent' | 'IncompletePaymentEvent' | 'SubscriptionPaymentEvent' | 'OpenBanking'
  seller: string
  amount: number
  paymentMethod: ManagedPaymentMethod
  transactions?: ManagedEventTransaction[]
  openBankingPaymentData?: OpenBankingPaymentData
  paymentSummary?: PaymentSummary | null
  conversionRates?: IConversionRate[]
  associatedEntity: AssociatedEntity
}

export interface Withdrawal extends LabelledEvent {
  gateway: 'Managed'
  name: 'Withdrawal'
  data: ManagedEventWithdrawalData
  managedMethod: ManagedPaymentMethod
}

export interface Deposit extends LabelledEvent {
  gateway: 'Managed'
  name: 'Deposit'
  data: ManagedEventDepositData
}

export interface Refund extends LabelledEvent {
  gateway: 'Managed'
  name: 'Refund'
  data: ManagedEventWithdrawalData
  managedMethod: ManagedPaymentMethod
}

export interface ManagedNewSubscriptionEvent extends LabelledEvent {
  gateway: 'Managed'
  name: 'NewSubscription'
  data: ManagedEventNewSubscription
  managedMethod: ManagedPaymentMethod
}

export interface ManagedSubscriptionCancelledEvent extends LabelledEvent {
  gateway: 'Managed'
  name: 'SubscriptionCancelled'
  data: ManagedEventSubscriptionCancelled
  managedMethod: ManagedPaymentMethod
}

export interface ManagedSubscriptionExpiredEvent extends LabelledEvent {
  gateway: 'Managed'
  name: 'SubscriptionExpired'
  data: ManagedEventSubscriptionExpired
  managedMethod: ManagedPaymentMethod
}

export type RadomEvent = ManagedPaymentEvent | Withdrawal |
  ManagedNewSubscriptionEvent | ManagedSubscriptionCancelledEvent | Refund | ManagedSubscriptionExpiredEvent | Deposit

export const parseManagedEvent = (managedEvent: ManagedEvent): RadomEvent | null => {
  switch (managedEvent.eventType) {
  case 'incompletePayment':
  case 'recurringPayment':
  case 'subscriptionPayment':
  case 'openBanking':
  case 'payment': {
    let associatedEntityId = ''
    let associatedEntityType = AssociatedEntityType['Payment session']
    let associatedEntityLink = ''

    const seller = ''
    const url = `/payment/${managedEvent.id}`

    const eventData = managedEvent.eventData
    const payment = eventData.payment || eventData.recurringPayment || eventData.incompletePayment ||
       eventData.subscriptionPayment || eventData.openBanking

    const paymentMethod = getMethod(payment.paymentMethod.network, payment.paymentMethod.token)

    const { checkoutSession, paymentLink, invoice, paymentSession, subscription, donationLink } = payment.radomData

    if (checkoutSession) {
      // Get checkout session
      associatedEntityId = checkoutSession.checkoutSessionId
      associatedEntityType = AssociatedEntityType.CheckoutSession
      associatedEntityLink = `/checkout_session/${checkoutSession.checkoutSessionId}`
    }

    if (paymentLink) {
      // Get payment link
      associatedEntityId = paymentLink.paymentLinkOrderId
      associatedEntityType = AssociatedEntityType.PaymentLink
      associatedEntityLink = `/payment_link/${paymentLink.paymentLinkId}`
    }

    if (invoice) {
      // Get payment link
      associatedEntityId = invoice.invoiceId
      associatedEntityType = AssociatedEntityType.Invoice
      associatedEntityLink = `/invoices/${invoice.invoiceId}`
    }

    if (paymentSession) {
      // Get payment session
      associatedEntityId = paymentSession.paymentSessionId
      associatedEntityType = AssociatedEntityType.PaymentSession
      associatedEntityLink = ''
    }

    if (subscription) {
      associatedEntityId = subscription.subscriptionId
      associatedEntityType = AssociatedEntityType.Subscription
      associatedEntityLink = `/subscription/${subscription.subscriptionId}`
    }

    if (donationLink) {
      associatedEntityId = donationLink.donationLinkOrderId
      associatedEntityType = AssociatedEntityType.DonationLink
      associatedEntityLink = ''
    }

    let name: any, label: any
    if (managedEvent.eventData.payment) {
      name = 'PaymentEvent'
      label = 'Payment'
    }
    if (managedEvent.eventData.recurringPayment) {
      name = 'RecurringPaymentEvent'
      label = 'Recurring payment'
    }
    if (managedEvent.eventData.incompletePayment) {
      name = 'IncompletePaymentEvent'
      label = 'Incomplete payment'
    }
    if (managedEvent.eventData.subscriptionPayment) {
      name = 'SubscriptionPaymentEvent'
      label = 'Subscription payment'
    }
    if (managedEvent.eventData.openBanking) {
      name = 'OpenBanking'
      label = 'Payment'
    }

    return {
      id: managedEvent.id,
      name,
      gateway: 'Managed',
      paymentMethod,
      date: new Date(managedEvent.eventDate),
      label,
      url,
      amount: Number(payment.amount),
      transactions: payment.transactions,
      openBankingPaymentData: payment.openBankingPaymentData,
      paymentSummary: payment.paymentSummary,
      conversionRates: payment.conversionRates,
      seller,
      associatedEntity: {
        id: associatedEntityId,
        entityType: associatedEntityType,
        link: associatedEntityLink
      }
    }
  }
  case 'withdrawal': {
    const withdrawal = managedEvent.eventData.withdrawal
    return {
      gateway: 'Managed',
      name: 'Withdrawal',
      date: new Date(managedEvent.eventDate),
      label: 'Withdrawal',
      data: withdrawal,
      url: `/withdrawal/${withdrawal.withdrawalRequestId}?source=transactions`,
      managedMethod: getMethod(withdrawal.network, withdrawal.token)
    }
  }
  case 'newSubscription': {
    return {
      gateway: 'Managed',
      name: 'NewSubscription',
      date: new Date(managedEvent.eventDate),
      label: 'New subscription',
      data: managedEvent.eventData.newSubscription,
      url: `/subscription/${managedEvent.eventData.newSubscription.subscriptionId}`,
      managedMethod: getMethod(
        managedEvent.eventData.newSubscription.network,
        managedEvent.eventData.newSubscription.token
      )
    }
  }
  case 'subscriptionCancelled': {
    return {
      gateway: 'Managed',
      name: 'SubscriptionCancelled',
      date: new Date(managedEvent.eventDate),
      label: 'Subscription cancelled',
      data: managedEvent.eventData.subscriptionCancelled,
      url: `/subscription/${managedEvent.eventData.subscriptionCancelled.subscriptionId}`,
      managedMethod: getMethod(
        managedEvent.eventData.subscriptionCancelled.network,
        managedEvent.eventData.subscriptionCancelled.token
      )
    }
  }
  case 'subscriptionExpired': {
    return {
      gateway: 'Managed',
      name: 'SubscriptionExpired',
      date: new Date(managedEvent.eventDate),
      label: 'Subscription expired',
      data: managedEvent.eventData.subscriptionExpired,
      url: `/subscription/${managedEvent.eventData.subscriptionExpired.subscriptionId}`,
      managedMethod: getMethod(
        managedEvent.eventData.subscriptionExpired.network,
        managedEvent.eventData.subscriptionExpired.token
      )
    }
  }
  case 'refund': {
    const refund = managedEvent.eventData.refund
    return {
      gateway: 'Managed',
      name: 'Refund',
      date: new Date(managedEvent.eventDate),
      label: 'Refund',
      data: refund,
      url: '/transactions',
      managedMethod: getMethod(refund.network, refund.token)
    }
  }
  case 'deposit': {
    const deposit = managedEvent.eventData.deposit
    return {
      gateway: 'Managed',
      name: 'Deposit',
      date: new Date(managedEvent.eventDate),
      label: 'Deposit',
      data: deposit,
      url: `/deposit/${deposit.depositId}?source=transactions`
    }
  }
  }

  return null
}

class Events {
  useRadomForEvents = true
  isLoading: boolean = false
  private activeEventsLoad?: { testMode: boolean, numDays: number }
  private queuedEventsLoad?: { testMode: boolean, numDays: number }
  private readonly pendingResolvers: Array<() => void> = []

  latestBlockScanned: { [chainID: number]: number } = {}
  computeEventsPromise: Promise<any>
  numWorkersAvailable: any = {}
  parallelFetchLogs: any = {}

  managedEvents: RadomEvent[] = []

  constructor() {
    makeAutoObservable(this)
  }

  loadEvents = async (testMode: boolean, numDays: number = 14): Promise<void> => {
    return await this.getEventsFromRadom(testMode, numDays)
  }

  private readonly getEventsFromRadom = async (testMode: boolean, numDays: number): Promise<void> => {
    if (this.isLoading) {
      const activeLoadAlreadyCoversRequest =
        this.activeEventsLoad?.testMode === testMode &&
        this.activeEventsLoad?.numDays >= numDays

      if (!activeLoadAlreadyCoversRequest) {
        this.queuedEventsLoad = { testMode, numDays }
      }

      return await new Promise<void>(resolve => {
        this.pendingResolvers.push(resolve)
      })
    }

    this.isLoading = true
    this.activeEventsLoad = { testMode, numDays }

    if (Radom.isLoggedIn) {
      try {
        const managedEvents = await Radom.listManagedEvents(
          testMode ? testNetworks : prodNetworks,
          startOfDay(subDays(new Date(), numDays)).toISOString(),
          new Date().toISOString()
        )
        this.managedEvents = managedEvents.data.map(e => parseManagedEvent(e)).filter(e => !!e) as RadomEvent[]
      } catch (err) {
        console.error('Failed to parse managed events: ', err)
      }
    }

    this.isLoading = false
    const completedLoad = this.activeEventsLoad
    this.activeEventsLoad = undefined

    const queuedLoad = this.queuedEventsLoad
    this.queuedEventsLoad = undefined

    if (queuedLoad &&
      (queuedLoad.testMode !== completedLoad?.testMode || queuedLoad.numDays > (completedLoad?.numDays ?? 0))) {
      await this.getEventsFromRadom(queuedLoad.testMode, queuedLoad.numDays)
    }

    const resolvers = this.pendingResolvers.splice(0)
    resolvers.forEach(resolve => resolve())
  }

  get allEvents(): RadomEvent[] {
    const events = [...this.managedEvents]
    events.sort((a, b) => b.date.getTime() - a.date.getTime())
    return events
  }
}

export default new Events()
